// Cari Posisi Barang menyeluruh (Rido): hasil pencarian global + Riwayat Perjalanan Pesanan. Baca-saja.
import { useEffect, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { Route, X, Truck, ScanLine, AlertTriangle } from "lucide-react";
import { OfflineDetailModal } from "./offline_monitor_detail";
import { MarketplaceBrand } from "./marketplace_brand";
import { ChannelMismatchNotice, openChannelFor } from "./channel";
import { GlobalFindMockup } from "./global_find_mockup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/production/track`;
export const fmtAt = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) || d.getFullYear() < 2015 ? "" : d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta" }) + " WIB";
};

function Position({ p, i, n }) {
  const ph = p.physical;
  return <li className="otr-pos" data-testid={`track-pos-${n}-${i}`}>
    <div className="otr-pos-head"><b>{p.product}{p.qty ? ` · ${p.qty} pcs` : ""}</b><span className="otr-div">{p.division || "—"}</span></div>
    <div className="otr-pos-status" data-testid={`track-pos-status-${n}-${i}`}>{p.status}</div>
    <div className="otr-meta">Operator: <b data-testid={`track-pos-pic-${n}-${i}`}>{p.pic || "Belum ditugaskan"}</b>{p.at && <> · {fmtAt(p.at)}</>}</div>
    <div className={`otr-basis ${ph ? "is-phys" : ""}`}>
      {ph ? <><ScanLine size={12}/> Fisik terkonfirmasi: {ph.label}{ph.by ? ` · ${ph.by}` : ""}{ph.at ? ` · ${fmtAt(ph.at)}` : ""}</>
        : "Posisi menurut catatan sistem — belum ada konfirmasi scan/serah-terima"}
    </div>
  </li>;
}

function Shipping({ s, n, shopee }) {
  if (!s) return null;
  return <div className={`otr-ship ${s.handed_over ? "is-handed" : ""}`} data-testid={`track-ship-${n}`}>
    <Truck size={14}/>
    <div>
      {shopee && <div>Ekspedisi: <b>{s.carrier || "—"}</b> · Resi: <b data-testid={`track-resi-${n}`}>{s.tracking_number || "—"}</b>
        {s.shopee_status && <> · Status Shopee: <b>{s.shopee_status}</b></>}</div>}
      <div>{s.note}{s.handed_over_at ? ` (${fmtAt(s.handed_over_at)})` : ""}</div>
      {shopee && s.last_synced_at && <div className="otr-meta">Sinkronisasi Shopee terakhir: {fmtAt(s.last_synced_at)}</div>}
    </div>
  </div>;
}

function TrackCard({ r, onJourney, withMockup }) {
  const n = r.order_number;
  return <article className="otr-card" data-src={r.source} data-testid={`track-result-${n}`}>
    <header className="otr-head">
      {r.channel && <MarketplaceBrand channel={r.channel} size="sm"/>}
      <strong className="otr-no" data-testid={`track-order-${n}`}>{n}</strong>
      <span className="otr-buyer">{r.customer_name}</span>
      {r.order_status && <span className="otr-status" data-testid={`track-status-${n}`}>{r.order_status}</span>}
      <button type="button" className="otr-btn" onClick={() => onJourney(r)} data-testid={`track-journey-btn-${n}`}><Route size={14}/> Lihat Perjalanan</button>
    </header>
    {r.items?.length > 0 && <p className="otr-items">{r.items.map(i => `${i.name}${i.variation ? ` (${i.variation})` : ""} × ${i.qty}`).join(" · ")}</p>}
    {withMockup && <GlobalFindMockup r={r}/>}
    {r.notice && <div className="otr-notice" data-testid={`track-notice-${n}`}><AlertTriangle size={13}/> {r.notice}</div>}
    {r.positions?.length > 0 && <ol className="otr-positions">{r.positions.map((p, i) => <Position key={i} p={p} i={i} n={n}/>)}</ol>}
    <Shipping s={r.shipping} n={n} shopee={r.source === "shopee"}/>
    {r.ship_by_date_iso && <div className="otr-meta" data-testid={`track-shipby-${n}`}>Batas kirim Shopee: <b>{fmtAt(r.ship_by_date_iso)}</b></div>}
    {r.updated_at && <div className="otr-meta">Pembaruan terakhir: {fmtAt(r.updated_at)}</div>}
  </article>;
}

export function ShopeeJourneyModal({ orderNumber, onClose, channel }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/shopee/${encodeURIComponent(orderNumber)}`, { withCredentials: true, timeout: 20000, headers: channel ? { "X-Channel": channel } : undefined })
      .then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.detail || "Gagal memuat perjalanan pesanan."));
  }, [orderNumber, channel]);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return createPortal(<div className="modal-overlay modal-top" onClick={onClose} data-testid="track-journey-overlay">
    <div className="modal-content otr-modal" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h3 data-testid="track-journey-title">Riwayat Perjalanan · {orderNumber}</h3>
        <button type="button" onClick={onClose} aria-label="Tutup" data-testid="track-journey-close"><X size={18}/></button>
      </div>
      <div className="modal-body">
        {err && <div className="otr-notice" role="alert" data-testid="track-journey-error">{err}</div>}
        {!d && !err && <p className="otr-meta">Memuat perjalanan…</p>}
        {d && <>
          <p className="otr-meta">{d.customer_name} · {d.order_status || "—"}</p>
          {d.notice && <div className="otr-notice">{d.notice}</div>}
          {d.journey.length === 0 ? <p className="otr-meta" data-testid="track-journey-empty">Belum ada kejadian tercatat.</p>
            : <ol className="otr-journey" data-testid="track-journey-list">{d.journey.map((e, i) => <li key={i} className={`otr-ev otr-ev-${e.kind}`} data-testid={`track-ev-${i}`}>
              <span className="otr-ev-div">{e.division}</span>
              <div><b>{e.title}</b>{e.product && <span className="otr-meta"> · {e.product}</span>}
                <div className="otr-meta">{e.who} · {fmtAt(e.at) || "waktu tidak tercatat"}{e.detail ? ` · ${e.detail}` : ""}</div></div>
            </li>)}</ol>}
          <p className="otr-meta">Hanya kejadian yang benar-benar tercatat di sistem ditampilkan. Tahap tanpa catatan tidak dianggap selesai.</p>
        </>}
      </div>
    </div>
  </div>, document.body);
}

export function OrderTrackResults({ q, source, withMockup = false }) {
  const [st, setSt] = useState({ loading: true, data: null, err: "" });
  const [open, setOpen] = useState(null);
  useEffect(() => {
    let alive = true;
    setSt(s => ({ ...s, loading: true, err: "" }));
    axios.get(`${API}/search`, { params: { q, source }, withCredentials: true, timeout: 20000 })
      .then(r => alive && setSt({ loading: false, data: r.data, err: "" }))
      .catch(e => alive && setSt({ loading: false, data: null, err: e?.response?.data?.detail || "Gagal mencari. Coba lagi." }));
    return () => { alive = false; };
  }, [q, source]);
  const res = st.data?.results || [];
  return <section className="otr" data-testid="track-results">
    <h4 className="pms-h">Hasil Pencarian Seluruh Pesanan {!st.loading && <span className="pms-total" data-testid="track-count">{res.length} ditemukan</span>}</h4>
    {st.loading && !st.data && <div className="monitor-v2-empty" data-testid="track-loading">Mencari di seluruh data pesanan…</div>}
    {st.err && <div className="otr-notice" role="alert" data-testid="track-error">{st.err}</div>}
    {!st.loading && !st.err && res.length === 0 && (st.data?.other_channel
      ? <ChannelMismatchNotice m={st.data.other_channel} testid="track-channel" onOpen={() => openChannelFor(["gudang"], st.data.other_channel.channel, "track", q, { gudang_section: "monitor" })}/>
      : <div className="monitor-v2-empty" data-testid="track-empty">{st.data?.reason || "Tidak ada hasil."}</div>)}
    <div className="otr-list">{res.map(r => <TrackCard key={r.order_number} r={r} onJourney={setOpen} withMockup={withMockup}/>)}</div>
    {open && (open.source === "shopee"
      ? <ShopeeJourneyModal orderNumber={open.order_number} channel={open.channel} onClose={() => setOpen(null)}/>
      : <OfflineDetailModal orderNumber={open.order_number} onClose={() => setOpen(null)}/>)}
  </section>;
}
